import { useCallback, useEffect, useState } from 'react';

// Not in lib.dom.d.ts yet.
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export type InstallState = 'unavailable' | 'installable' | 'installed' | 'ios-manual';

function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    // iOS Safari's own non-standard flag; not in TS's Navigator type.
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

function isIos(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !('MSStream' in window);
}

/**
 * Wraps the `beforeinstallprompt` flow (Chromium/Android/desktop Chrome and
 * Edge only — no such API exists on iOS or Firefox). On iOS there is no way
 * to trigger the install prompt from a page at all, so callers get
 * 'ios-manual' and should show instructions ("Share, then Add to Home
 * Screen") instead of a button.
 */
export function useInstallPrompt() {
  const [deferredEvent, setDeferredEvent] = useState<BeforeInstallPromptEvent | null>(
    null,
  );
  const [state, setState] = useState<InstallState>('unavailable');

  useEffect(() => {
    if (isStandalone()) {
      setState('installed');
      return;
    }
    if (isIos()) {
      setState('ios-manual');
      return;
    }

    const onBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      setDeferredEvent(event as BeforeInstallPromptEvent);
      setState('installable');
    };
    const onInstalled = () => {
      setDeferredEvent(null);
      setState('installed');
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferredEvent) return;
    await deferredEvent.prompt();
    const { outcome } = await deferredEvent.userChoice;
    setDeferredEvent(null);
    setState(outcome === 'accepted' ? 'installed' : 'unavailable');
  }, [deferredEvent]);

  return { state, promptInstall };
}
